import * as Illustration from "@npco/zeller-design-system/illustration";
import type { ADT } from "pb.adt";

import * as transport from "../../../transport";
import { Layout } from "../shared/layout";
import {
	NETWORK_ERROR_MESSAGE,
	NetworkErrorPrompt,
} from "../shared/network-error-prompt";
import { Prompt } from "../shared/prompt";
import type { Page } from "../terminal-setup";

export function PageError({
	page,
	onLogout,
	pending,
}: {
	page?: ADT.Pick<Page, "Error">;
} & Required<Pick<Layout.Props, "onLogout" | "pending">>) {
	if (page?.type === "network") {
		return (
			<Layout
				actionsStyle="full-width"
				logo
				onClose={() =>
					void transport.Resolve.Error("Network Failure", {
						message: NETWORK_ERROR_MESSAGE,
					})
				}
				pending={pending}
				actions={
					page?.onRetry && [{ label: "Try Again", onClick: page.onRetry }]
				}
			>
				<NetworkErrorPrompt />
			</Layout>
		);
	}

	const { title, message, onClose } = getErrorMessages(page?.type);

	return (
		<Layout logo onClose={onClose} onLogout={onLogout} pending={pending}>
			<Prompt
				image={<Illustration.ErrorBalance />}
				title={title}
				message={message}
				action={page?.onRetry && { label: "Try Again", onClick: page.onRetry }}
			/>
		</Layout>
	);
}

function getErrorMessages(type: ADT.Pick<Page, "Error">["type"]): {
	title: string;
	message: string;
	onClose: () => void;
} {
	if (type === "request") {
		const message = "Please ensure you're using a compatible SDK version.";
		return {
			title: "Protocol failure.",
			message,
			onClose: () =>
				void transport.Resolve.Error("Operation Request Invalid", { message }),
		};
	} else if (type === "no-entities") {
		const message =
			"Your account isn't linked to a business. Please contact support to get set up.";
		return {
			title: "No business found.",
			message,
			onClose: () =>
				void transport.Resolve.Error("Setup Required", { message }),
		};
	} else {
		const message = "Please try again, or contact support if problem persists.";
		return {
			title: "Something went wrong.",
			message,
			onClose: () =>
				void transport.Resolve.Error("Operation Failure", { message }),
		};
	}
}
